import { useEffect, useState } from "react";
import { getCallReport, getCallReportList } from "@/lib/apiClient";
import { CallRow } from "@/components/CallHistory";
import { useAuth } from "@/context/AuthContext";
import { BarChart3, ChevronDown } from "lucide-react";

const PERIODS = [{ id: "today", label: "Vandaag" }, { id: "7d", label: "7 dagen" }, { id: "30d", label: "30 dagen" }];
const fmtDur = (s) => (s ? `${Math.floor(s / 60)}:${String(s % 60).padStart(2, "0")}` : "—");
const COLS = [
  { k: "total", label: "Gesprekken" }, { k: "answered", label: "Beantwoord" }, { k: "missed", label: "Gemist" },
  { k: "voicemail", label: "Voicemail" }, { k: "avg_duration_sec", label: "Gem. duur", fmt: fmtDur },
];

function CallList({ period, userId }) {
  const [calls, setCalls] = useState(null);
  useEffect(() => { setCalls(null); getCallReportList(period, userId).then(setCalls).catch(() => setCalls([])); }, [period, userId]);
  if (!calls) return <p className="text-sm text-[#5B6675]">Gesprekken laden…</p>;
  if (!calls.length) return <p className="text-sm text-[#5B6675]">Geen gesprekken.</p>;
  return <div className="space-y-2 max-h-[520px] overflow-y-auto fe-scroll" data-testid={`report-calls-${userId || "all"}`}>{calls.map((c) => <CallRow key={c.id} c={c} showLead />)}</div>;
}

export function CallReport() {
  const [period, setPeriod] = useState("7d");
  const [data, setData] = useState(null);
  const [open, setOpen] = useState(null);
  const { user } = useAuth();
  const title = user?.role === "admin" ? "Belrapportage" : user?.role === "teamleider" ? "Belrapportage team" : "Mijn gesprekken";
  useEffect(() => { setData(null); setOpen(null); getCallReport(period).then(setData).catch(() => setData({ rows: [], totals: {} })); }, [period]);
  const toggle = (k) => setOpen((o) => (o === k ? null : k));

  return (
    <div className="rounded-2xl border border-[#E2E7EC] bg-white overflow-hidden mb-8" data-testid="call-report">
      <div className="px-6 py-4 border-b border-[#E2E7EC] flex flex-wrap items-center gap-3">
        <BarChart3 className="h-5 w-5 text-[#16BFBF]" strokeWidth={1.75} />
        <h2 className="font-heading font-semibold text-xl text-[#16202B]" data-testid="call-report-title">{title}</h2>
        <div className="ml-auto inline-flex rounded-xl border border-[#E2E7EC] p-1">
          {PERIODS.map((p) => (
            <button key={p.id} data-testid={`report-period-${p.id}`} onClick={() => setPeriod(p.id)}
              className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${period === p.id ? "bg-[#001C51] text-white" : "text-[#5B6675] hover:bg-[#EDF1F5]"}`}>{p.label}</button>
          ))}
        </div>
      </div>
      {!data ? <p className="px-6 py-8 text-sm text-[#5B6675]">Laden…</p>
        : data.rows.length === 0 ? <p className="px-6 py-8 text-sm text-[#5B6675]" data-testid="call-report-empty">Nog geen Rinkel-gesprekken in deze periode.</p> : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead><tr className="text-left text-[#5B6675] bg-[#F6F9FB]">
                <th className="px-6 py-3 font-medium">Medewerker</th>
                {COLS.map((c) => <th key={c.k} className="px-4 py-3 font-medium text-right">{c.label}</th>)}
              </tr></thead>
              <tbody>
                {data.rows.map((r) => { const k = r.user_id || "_none"; return [
                  <tr key={k} data-testid={`report-row-${r.user_id || "none"}`} onClick={() => toggle(k)} className="border-t border-[#E2E7EC] cursor-pointer hover:bg-[#F6F9FB]">
                    <td className="px-6 py-3 font-medium text-[#16202B]"><span className="inline-flex items-center gap-2"><ChevronDown className={`h-4 w-4 text-[#5B6675] transition-transform ${open === k ? "rotate-180" : ""}`} />{r.user_name}</span></td>
                    {COLS.map((c) => <td key={c.k} className={`px-4 py-3 text-right tabular-nums ${c.k === "missed" && r.missed ? "text-[#B0413E] font-semibold" : "text-[#16202B]"}`}>{c.fmt ? c.fmt(r[c.k]) : r[c.k]}</td>)}
                  </tr>,
                  open === k && <tr key={`${k}-calls`}><td colSpan={COLS.length + 1} className="px-6 py-4 bg-[#F6F9FB]"><CallList period={period} userId={k} /></td></tr>,
                ]; })}
                <tr className="border-t-2 border-[#E2E7EC] bg-[#F6F9FB] font-semibold cursor-pointer" data-testid="report-totals" onClick={() => toggle("_all")}>
                  <td className="px-6 py-3 text-[#001C51]"><span className="inline-flex items-center gap-2"><ChevronDown className={`h-4 w-4 transition-transform ${open === "_all" ? "rotate-180" : ""}`} />Totaal · alle gesprekken</span></td>
                  {COLS.map((c) => <td key={c.k} className="px-4 py-3 text-right tabular-nums text-[#001C51]">{c.k === "avg_duration_sec" ? "" : data.totals[c.k]}</td>)}
                </tr>
                {open === "_all" && <tr><td colSpan={COLS.length + 1} className="px-6 py-4 bg-[#F6F9FB]"><CallList period={period} userId="" /></td></tr>}
              </tbody>
            </table>
          </div>
        )}
    </div>
  );
}
